// src/App.jsx
import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/stores/uiStore'
import ProtectedRoute from '@/components/ProtectedRoute'
import LoadingScreen from '@/components/LoadingScreen'
import ErrorBoundary from '@/components/ErrorBoundary'
import PWAUpdatePrompt from '@/components/PWAUpdatePrompt'
import connectionManager from '@/services/connectionManager'

// Lazy-loaded route components
const AuthPage = lazy(() => import('@/components/auth/AuthPage'))
const Dashboard = lazy(() => import('@/components/dashboard/Dashboard'))
const AdminConsole = lazy(() => import('@/components/admin/AdminConsole'))
const UserSettings = lazy(() => import('@/components/settings/UserSettings'))
const SupportPage = lazy(() => import('@/components/support/SupportPage'))
const DeveloperPage = lazy(() => import('@/components/developer/DeveloperPage'))
const LandingPage = lazy(() => import('@/components/LandingPage'))
const HelpPage = lazy(() => import('@/components/HelpPage'))

function App() {
    const { isAuthenticated, isLoading, user, initialize } = useAuth()
    const { initializeTheme } = useUIStore()

    useEffect(() => {
        // Initialize auth state from localStorage
        initialize()

        // Initialize theme
        initializeTheme()

        // Initialize connection manager (offline-first, hub discovery)
        connectionManager.init().catch(err =>
            console.warn('Connection manager init:', err)
        )

        return () => connectionManager.destroy()
    }, [])

    if (isLoading) {
        return <LoadingScreen />
    }

    return (
        <ErrorBoundary>
            <Router>
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
                    <Suspense fallback={<LoadingScreen />}>
                        <Routes>
                            {/* Public landing page */}
                            <Route
                                path="/welcome"
                                element={<LandingPage />}
                            />
                            {/* Public help page */}
                            <Route
                                path="/help"
                                element={<HelpPage />}
                            />
                            <Route
                                path="/login"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to="/dashboard" replace />
                                    ) : (
                                        <AuthPage />
                                    )
                                }
                            />
                            {/* User settings */}
                            <Route
                                path="/settings"
                                element={
                                    <ProtectedRoute>
                                        <UserSettings />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Support tickets */}
                            <Route
                                path="/support"
                                element={
                                    <ProtectedRoute>
                                        <SupportPage />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Developer API */}
                            <Route
                                path="/developer"
                                element={
                                    <ProtectedRoute>
                                        <DeveloperPage />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Admin console - requires admin role */}
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminConsole />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    <ProtectedRoute>
                                        <Dashboard />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Root: show landing if not authenticated, dashboard if authenticated */}
                            <Route
                                path="/"
                                element={
                                    isAuthenticated ? (
                                        <Navigate to="/dashboard" replace />
                                    ) : (
                                        <Navigate to="/welcome" replace />
                                    )
                                }
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>

                    {/* Toast notifications */}
                    <Toaster
                        position="top-right"
                        toastOptions={{
                            duration: 4000,
                            style: {
                                background: 'var(--toast-bg)',
                                color: 'var(--toast-color)',
                                border: '1px solid var(--toast-border)'
                            },
                            success: {
                                iconTheme: {
                                    primary: '#22c55e',
                                    secondary: '#ffffff'
                                }
                            },
                            error: {
                                iconTheme: {
                                    primary: '#ef4444',
                                    secondary: '#ffffff'
                                }
                            }
                        }}
                    />

                    {/* PWA Update Prompt */}
                    <PWAUpdatePrompt />
                </div>
            </Router>
        </ErrorBoundary>
    )
}

export default App
